import { CollectionHelper } from 'src/app/helpers/collection-helper';
import ProductHelper from 'src/app/helpers/product-helper';
import ProjectHelper from 'src/app/helpers/project-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import IPBoard from 'src/app/model/product/ip-board/ip-board';
import IPBoardPart from 'src/app/model/product/ip-board/ip-board-part';
import { IPBoardPlacementTypeChoices } from 'src/app/model/product/ip-board/ip-board-placement-type';
import Product from 'src/app/model/product/product';
import { ProductTypes } from 'src/app/model/product/product-type';
import {
  ClientWindowSticker,
  CLIENT_WINDOW_STICKER_TYPE
} from 'src/app/model/product/windowsticker/client-windowsticker';
import { isStandardStickerPart } from 'src/app/model/product/windowsticker/standard-sticker-part';
import { isStickerPart } from 'src/app/model/product/windowsticker/sticker-part';
import { BaseProject } from 'src/app/model/project/base-project';
import { Project } from 'src/app/model/project/project';

export class CreateViewBaseProjectHelper {
  private constructor() {}

  public static validateClientProductConfiguration(
    products: Product[]
  ): string {
    if (!CollectionHelper.isPopulated(products)) {
      return null;
    }

    let toReturn = null;

    for (const product of products) {
      if (product.type === null) {
        continue;
      }

      switch (product.type) {
        case CLIENT_WINDOW_STICKER_TYPE:
          toReturn = this.validateClientWindowSticker(<ClientWindowSticker>(
            product
          ));
          break;
        case ProductTypes.IP_BOARD.value:
          toReturn = this.validateClientIPBoard(<IPBoard>product);
          break;
      }

      if (toReturn != null) {
        break;
      }
    }

    return toReturn;
  }

  private static validateClientWindowSticker(
    windowSticker: ClientWindowSticker
  ): string {
    if (windowSticker.foil == null) {
      return 'Gelieve een materiaal te kiezen';
    }
    if (windowSticker.laminate == null) {
      return 'Gelieve een keuze te maken voor laminaat';
    }

    for (const stickerPart of windowSticker.clientStickerParts) {
      if (stickerPart.instance == null) {
        return 'Gelieve een type te kiezen voor elke deelsticker';
      }
      if (isStandardStickerPart(stickerPart.instance)) {
        if (stickerPart.instance.productSize == null) {
          return 'Gelieve een formaat te kiezen voor elke deelsticker met standaardafmetingen';
        }
      } else if (!isStickerPart(stickerPart.instance)) {
        return 'Gelieve een type te kiezen voor elke deelsticker';
      }
    }
  }

  private static validateClientIPBoard(ipBoard: IPBoard): string {
    let ipBoardPartValidation =
      ipBoard.boardPart == null
        ? null
        : this.validateClientIPBoardPart(ipBoard.boardPart);
    if (ipBoardPartValidation != null) {
      return ipBoardPartValidation;
    } else if (!ipBoard.leftRightBoard && ipBoard.otherSideBoardPart != null) {
      // this should not be possible, but including just in case
      return 'Kan enkel twee verschillende borden kiezen als er expliciet is gekozen voor een combinatie van twee borden';
    } else {
      return ipBoard.otherSideBoardPart == null
        ? null
        : this.validateLeftRightBoardPart(ipBoard.otherSideBoardPart);
    }
  }

  private static validateLeftRightBoardPart(ipBoardPart: IPBoardPart): string {
    if (!ipBoardPart.signOptionSelected) {
      return 'Gelieve een rechter bord te kiezen';
    }
  }

  private static validateClientIPBoardPart(ipBoardPart: IPBoardPart): string {
    if (!ipBoardPart.signOptionSelected) {
      return 'Gelieve een bord te kiezen';
    }
    if (
      ipBoardPart.sign == null &&
      (ipBoardPart.otherIPBoardPartType == null ||
        ipBoardPart.otherHeight == null ||
        ipBoardPart.otherWidth == null)
    ) {
      return 'Gelieve afmetingen te kiezen/in te vullen';
    }
    if (ipBoardPart.boardType == null) {
      return 'Gelieve een type bord te kiezen';
    }
    if (ipBoardPart.placementType == null) {
      return 'Gelieve een type plaatsing te kiezen';
    }
    if (
      ipBoardPart.placementType.ordinal ===
        IPBoardPlacementTypeChoices.OTHER.ordinal &&
      StringHelper.isEmpty(ipBoardPart.alternativePlacementType)
    ) {
      return 'Gelieve de andere plaatsingsmethode te specificeren';
    }
    return null;
  }

  public static validateProjectPlacement(
    baseProject: BaseProject,
    project: Project,
    projectHasIPBoard: boolean,
    minPlacementDate: Date,
    higherFloorOptionSelected: boolean
  ): string {
    if (
      CollectionHelper.isPopulated(project.products) &&
      baseProject.projectPlacement != null &&
      StringHelper.isEmpty(baseProject.projectPlacement.placementFloor) &&
      project.products.some(p =>
        ProductHelper.placementFloorRequiredForProduct(p)
      )
    ) {
      return 'Gelieve een keuze te maken voor verdieping';
    }
    if (
      higherFloorOptionSelected &&
      baseProject.projectPlacement != null &&
      StringHelper.isEmpty(baseProject.projectPlacement.placementFloor)
    ) {
      return 'Gelieve de hogere verdieping in te vullen';
    }
    if (
      projectHasIPBoard &&
      baseProject.projectPlacement != null &&
      ProjectHelper.mustHaveAppointment(baseProject.projectPlacement) &&
      !(
        baseProject.projectPlacement.appointment ||
        baseProject.projectPlacement.collectKey
      )
    ) {
      return 'Gelieve een afspraak aan te vragen of aan te geven dat er een sleutel moet worden afgehaald';
    }
    if (
      !ProjectHelper.isPlacementEarliestDateValidForClient(
        baseProject,
        minPlacementDate
      )
    ) {
      return 'Gelieve een plaatsingsdatum te kiezen minstens vier werkdagen later dan vandaag';
    }
    if (!this.validatePoleColor(project)) {
      return 'Gelieve kleur hout te selecteren';
    }
    return null;
  }

  private static validatePoleColor(project: Project): boolean {
    const poleColorRequiredButMissing = !CollectionHelper.isPopulated(
      project.products
    )
      ? null
      : project.products.find(p => {
          if (!ProductHelper.isIPBoard(p)) {
            return false;
          }
          const ipBoard = <IPBoard>p;
          return (
            ipBoard.boardPart != null &&
            ipBoard.boardPart.sign == null &&
            ipBoard.boardPart.placementType != null &&
            p.poleColor == null
          );
        });
    return poleColorRequiredButMissing == null;
  }

  public static validateExtraCompanyContacts(project: Project): boolean {
    const hasErroringExtraContact =
      CollectionHelper.isPopulated(project.projectExtraCompanyContacts) &&
      project.projectExtraCompanyContacts.some(
        e =>
          StringHelper.isEmptyOrWhiteSpace(e.company) ||
          StringHelper.isEmptyOrWhiteSpace(e.contact)
      );
    return !project.collaborative || !hasErroringExtraContact;
  }
}
